import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {setPlayerAuth} from '../../redux/actions';
import './UserNamePage.css';
import {createRandomId} from "../../utils/ids/createRandomId";
import {LoadedImage} from "../EmblaCarousel/FactionsCarousel";
import {usePageAnalytics} from '../../hooks/analytics/usePageAnalytics';
import PageButton from "../elements/PageButton/PageButton";
import {API_BASE} from '../../utils/apiConfig';
import VersionLabel from '../VersionLabel/VersionLabel';
import MusicMuteToggleButton from '../MusicMuteToggleButton/MusicMuteToggleButton';
import {FRIEND_REFERAL_STORAGE_KEY, PARTNER_REFERAL_STORAGE_KEY} from '../../constants/partner';
import {setVersionAutoReloadSuspended} from '../../utils/version/checkVersion';
import {trackAdGoal, AD_GOALS} from '../../analytics/adTracking';
import {getNicknameError} from '../../configs/nickname';

function UserNamePage({onAuth, onboarding = false, devMode = false}) {
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  // Правила общие с сервером и с админской сменой никнейма — см. configs/nickname
  const validateName = (value) => getNicknameError(value);

  usePageAnalytics('AuthPage');

  // Пока пользователь вводит никнейм, автообновление по расхождению версий
  // запрещено: перезагрузка стирает введенное имя, и регистрация уходит с
  // именем из аккаунта Яндекса — тем самым, которое пользователь и менял.
  // Расхождение никуда не денется: его подберет ближайшая проверка после
  // ухода с этого экрана
  useEffect(() => {
    setVersionAutoReloadSuspended(true);
    return () => setVersionAutoReloadSuspended(false);
  }, []);

  useEffect(() => {
    if (onboarding) return;

    const savedName = localStorage.getItem('playerName');
    const savedId = localStorage.getItem('playerId');
    if (savedName && savedId) {
      dispatch(setPlayerAuth({name: savedName, id: savedId}));
      onAuth({name: savedName, id: savedId});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Регистрация по реферальной ссылке — партнерской или «Пригласи друга»:
  // идентификаторы записаны в localStorage при открытии сайта по
  // /referal/partner/{id} либо /referal/friend/{userId} (см. App) — именно
  // оттуда, а не из текущего URL: тот сбивается редиректами авторизации
  // Яндекса. Общее для ОБОИХ путей регистрации — и через Яндекс, и dev-входа
  const getPartnerReferalIdPayload = () => {
    const partnerReferalId = localStorage.getItem(PARTNER_REFERAL_STORAGE_KEY);
    const friendReferalUserId = localStorage.getItem(FRIEND_REFERAL_STORAGE_KEY);
    return {
      ...(partnerReferalId ? {partnerReferalId} : {}),
      ...(friendReferalUserId ? {friendReferalUserId} : {}),
    };
  };

  const completeDevLogin = async (trimmedName) => {
    const res = await fetch(`${API_BASE}/auth/dev-login`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      credentials: 'include',
      body: JSON.stringify({
        name: trimmedName,
        ...getPartnerReferalIdPayload(),
      }),
    });

    if (!res.ok) {
      let message = 'Не удалось создать dev-пользователя';
      try {
        const errorData = await res.json();
        if (errorData.error) {
          message = errorData.error;
        }
      } catch {
        // Ignore parse errors.
      }
      throw new Error(message);
    }

    const data = await res.json();
    // Идентификатор отработал — чистим, чтобы не влиять на будущие регистрации
    // с этого браузера
    localStorage.removeItem(PARTNER_REFERAL_STORAGE_KEY);
    localStorage.removeItem(FRIEND_REFERAL_STORAGE_KEY);
    return data.user;
  };

  const completeOnboarding = async (trimmedName) => {
    const res = await fetch(`${API_BASE}/auth/complete-onboarding`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      credentials: 'include',
      body: JSON.stringify({
        name: trimmedName,
        ...getPartnerReferalIdPayload(),
      }),
    });

    if (!res.ok) {
      let message = 'Не удалось сохранить никнейм';
      try {
        const errorData = await res.json();
        if (errorData.error) {
          message = errorData.error;
        }
      } catch {
        // Ignore parse errors.
      }
      throw new Error(message);
    }

    const data = await res.json();
    // Идентификатор отработал (привязка выполнена на сервере, если партнер
    // найден и активен) — чистим, чтобы не влиять на будущие регистрации
    // с этого браузера
    localStorage.removeItem(PARTNER_REFERAL_STORAGE_KEY);
    localStorage.removeItem(FRIEND_REFERAL_STORAGE_KEY);
    return data.user;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    const validationError = validateName(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (devMode) {
      try {
        const user = await completeDevLogin(trimmed);
        localStorage.setItem('playerName', user.name);
        localStorage.setItem('playerId', user.id);
        dispatch(setPlayerAuth(user));
        onAuth(user);
      } catch (error) {
        setError(error.message);
      }
      return;
    }

    if (onboarding) {
      try {
        const user = await completeOnboarding(trimmed);
        localStorage.setItem('playerName', user.name);
        localStorage.setItem('playerId', user.id);
        // Регистрация: аккаунт создан прямо сейчас. Это главная конверсия
        // рекламных кампаний, userid — для кросс-девайс матчинга у VK
        trackAdGoal(AD_GOALS.REGISTRATION, {userId: user.id});
        dispatch(setPlayerAuth(user));
        onAuth(user);
      } catch (error) {
        setError(error.message);
      }
      return;
    }

    const id = createRandomId(trimmed + '_id');
    localStorage.setItem('playerName', trimmed);
    localStorage.setItem('playerId', id);
    dispatch(setPlayerAuth({name: trimmed, id}));
    onAuth({name: trimmed, id});
  };

  return (
    <>
      <MusicMuteToggleButton pageId={'AuthPage'}/>
      <LoadedImage
        className={'loading-screen-bust main-menu-bust margin-top-auto'}
        src={'/img/other/strategikon_bust.png'}
      />
      <h1 className="auth-page-title">Стратегикон</h1>
      <form className="auth-form" onSubmit={handleSubmit}>
        <label className="auth-label">
          {devMode ? 'Введите имя (dev):' : onboarding ? 'Введите игровой никнейм:' : 'Введите имя:'}
        </label>
        <input
          className="auth-input"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          maxLength={20}
          autoFocus
        />
        {error && <p className="auth-error">{error}</p>}
        <PageButton type="submit" pageId="UserNamePage" buttonId="submit">
          {devMode ? 'Сохранить' : onboarding ? 'Сохранить' : 'Войти'}
        </PageButton>
      </form>
      <div className="auth-page-copyright">
        {`™ Стратегикон, ${new Date().getFullYear()}, все права защищены`}
      </div>
      <VersionLabel/>
    </>
  );
}

export default UserNamePage;
